import { useState } from "react"

import type { MenuGridItem } from "@/components/features/menu/menu-grid"
import { Button } from "@/components/ui/button"
import {
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { Label } from "@/components/ui/label"
import { ExtraGroupSelector, selectedExtraCount } from "@/features/staff/extra-group-selector"
import { groupSelectionValid } from "@/features/staff/extras-workspace"
import { t } from "@/lib/i18n"

const I18N_BASE = "ui.react.features.staff.table_detail_workspace"

export type OrderDraftErrors = Record<string, string | string[] | undefined>

/** What one line of an order looks like while it is still being composed. */
export type OrderDraft = {
  item: MenuGridItem | null
  extraIds: string[]
  quantity: number
  notes: string
}

const EMPTY_DRAFT: OrderDraft = { item: null, extraIds: [], quantity: 1, notes: "" }

function formatPrice(price: string | number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency || "MXN").toUpperCase(),
  }).format(Number(price))
}

export function errorMessage(value: string | string[] | undefined): string | null {
  return Array.isArray(value) ? value[0] ?? null : value ?? null
}

/**
 * Composing one line: which extras, how many, any note.
 *
 * Shared by the table workspace and the counter workspace, which is the point —
 * they are the same job on two different accounts, and the extras rules in
 * particular have exactly one correct reading (`groupSelectionValid`). Two
 * copies of this drawer would be two places for "is this selection legal" to
 * drift from what `Location::ItemModifierSelection` decides on the server.
 *
 * Purely presentational: the draft and the submit both belong to whichever
 * workspace mounted it, because only that workspace knows what account the line
 * lands on.
 */
export function OrderItemDrawer({
  open,
  onOpenChange,
  draft,
  onDraftChange,
  onSubmit,
  submitting,
  errors = {},
  blockedReason = null,
  testIdPrefix,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  draft: OrderDraft
  onDraftChange: (draft: OrderDraft) => void
  onSubmit: () => void
  submitting: boolean
  errors?: OrderDraftErrors
  /**
   * Why this line cannot be rung up right now, or null. Today the only sender
   * is a closed cash drawer (`Location::CashRegisterGate`), which is why this is
   * a sentence rather than a boolean: the reason has to reach the waiter, and a
   * disabled button with no explanation is how someone ends up phoning their
   * manager mid-service. The server refuses either way — this only stops the
   * drawer from taking a draft it will then throw away.
   */
  blockedReason?: string | null
  /** Keeps each workspace's existing testids stable — `table-detail`, `counter`. */
  testIdPrefix: string
}) {
  const { item, extraIds, quantity, notes } = draft
  const extraGroups = item?.extraGroups || []
  const extraGroupsValid = extraGroups.every((group) =>
    groupSelectionValid(group, selectedExtraCount(group, extraIds))
  )

  return (
    <ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="flex-1 overflow-y-auto">
          <DrawerHeader className="pb-2">
            <DrawerTitle>{item?.name ?? t("ui.menu.item_fallback")}</DrawerTitle>
            {item?.description ? <DrawerDescription>{item.description}</DrawerDescription> : null}
            {item ? (
              <p className="pt-1 text-sm font-semibold">{formatPrice(item.price, item.currency)}</p>
            ) : null}
          </DrawerHeader>

          <div className="space-y-5 px-4 pb-2">
            {/* The bill error used to render next to the target selector, so
                removing that block would have dropped it entirely. Note this
                is not yet a working channel: `render_failed_create` returns
                the bag under `orderForm.ordersErrors` rather than Inertia's
                `errors`, so `postInline` resolves and the drawer closes over
                this on a 422. Making order failures visible is its own change. */}
            {blockedReason ? (
              <div
                className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
                data-testid={`${testIdPrefix}-order-blocked`}
              >
                {blockedReason}
              </div>
            ) : null}

            {errorMessage(errors.base) || errorMessage(errors.bill) ? (
              <div className="border-destructive/20 bg-destructive/5 text-destructive space-y-1 rounded-md border p-3 text-sm">
                {errorMessage(errors.base) ? <p>{errorMessage(errors.base)}</p> : null}
                {errorMessage(errors.bill) ? <p>{errorMessage(errors.bill)}</p> : null}
              </div>
            ) : null}

            <ExtraGroupSelector
              groups={extraGroups}
              extraIds={extraIds}
              onChange={(nextExtraIds) => onDraftChange({ ...draft, extraIds: nextExtraIds })}
              currency={item?.currency ?? "mxn"}
              heading={t(`${I18N_BASE}.extras`)}
              error={errorMessage(errors.modifiers)}
            />

            <div className="space-y-2">
              <p className="text-sm font-medium">{t(`${I18N_BASE}.quantity`)}</p>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border text-xl font-medium hover:bg-muted/50 disabled:opacity-40"
                  onClick={() => onDraftChange({ ...draft, quantity: Math.max(1, quantity - 1) })}
                  disabled={quantity <= 1}
                  aria-label={t(`${I18N_BASE}.decrease_quantity`)}
                >
                  −
                </button>
                <span className="flex-1 text-center text-2xl font-bold tabular-nums">{quantity}</span>
                <button
                  type="button"
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border text-xl font-medium hover:bg-muted/50"
                  onClick={() => onDraftChange({ ...draft, quantity: quantity + 1 })}
                  aria-label={t(`${I18N_BASE}.increase_quantity`)}
                >
                  +
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${testIdPrefix}-notes`} className="text-sm font-medium">
                {t(`${I18N_BASE}.notes`)}
              </Label>
              <textarea
                id={`${testIdPrefix}-notes`}
                value={notes}
                onChange={(event) => onDraftChange({ ...draft, notes: event.target.value })}
                rows={2}
                className="border-input focus-visible:border-ring focus-visible:ring-ring/50 flex min-h-[72px] w-full resize-none rounded-lg border bg-transparent px-3 py-2.5 text-sm shadow-xs outline-none focus-visible:ring-[3px]"
                placeholder={t(`${I18N_BASE}.notes_placeholder`)}
              />
            </div>
          </div>
        </div>

        <DrawerFooter className="pt-2">
          <Button
            type="button"
            className="h-12 w-full text-base"
            onClick={onSubmit}
            disabled={submitting || !extraGroupsValid || blockedReason !== null}
            data-testid={`${testIdPrefix}-add-order-button`}
          >
            {t(`${I18N_BASE}.add_order`)}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
            {t(`${I18N_BASE}.cancel`)}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </ResponsiveDrawer>
  )
}

/**
 * The draft a workspace hands to `OrderItemDrawer`, plus the open/close plumbing
 * that always comes with it. Extracted alongside the drawer so the two
 * workspaces cannot disagree about when a draft is reset — closing mid-submit
 * must NOT clear it, or a failed post would come back to an empty drawer.
 */
export function useOrderDraft(submitting: boolean) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<OrderDraft>(EMPTY_DRAFT)

  const reset = () => setDraft(EMPTY_DRAFT)

  const openWith = (item: MenuGridItem) => {
    setDraft({ ...EMPTY_DRAFT, item })
    setOpen(true)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen && !submitting) reset()
  }

  return { open, setOpen, draft, setDraft, reset, openWith, handleOpenChange }
}
